import React, { useEffect, useState, useRef } from 'react';
import { Brain, RefreshCw, Trash2, Plus, Globe, Server, FileText, ChevronRight, X } from 'lucide-react';
import api from '../../services/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import alertService from '../../utils/alert';

const STATUS_BADGE = {
  pending:    'bg-[#F3F2F2] text-[#706E6B]',
  processing: 'bg-[#FFF4E5] text-[#8C4B02]',
  syncing:    'bg-[#FFF4E5] text-[#8C4B02]',
  ready:      'bg-[#E3F3E8] text-[#036B3A]',
  failed:     'bg-[#FDE7E9] text-[#BA0517]',
  idle:       'bg-[#F3F2F2] text-[#706E6B]',
};

const SOURCE_TYPES = [
  {
    key: 'document',
    label: 'Documents',
    description: 'Upload PDF, DOCX, TXT, CSV or Excel files to index',
    icon: FileText,
    color: 'text-[#5C5C5C]',
    bg: 'bg-[#F7F9FB]',
  },
  {
    key: 'web',
    label: 'Website',
    description: 'Read the pages of a public website (sitemap first) and keep them fresh',
    icon: Globe,
    color: 'text-[#2E844A]',
    bg: 'bg-[#E3F3E8]',
  },
  {
    key: 'rest',
    label: 'REST API',
    description: 'Pull and index data from any HTTP endpoint',
    icon: Globe,
    color: 'text-[#0176D3]',
    bg: 'bg-[#EAF4FE]',
  },
  {
    key: 'mcp',
    label: 'MCP Server',
    description: 'Index resources from an MCP-compatible server',
    icon: Server,
    color: 'text-[#0176D3]',
    bg: 'bg-[#EEF4FF]',
  },
];

// ── Reusable field components ────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#706E6B] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', className = '' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] ${className}`}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function AuthFields({ authType, onAuthType, authKeyName, onAuthKeyName, authValue, onAuthValue }) {
  return (
    <>
      <Field label="Auth Type">
        <Select value={authType} onChange={onAuthType} options={[
          { value: 'none',   label: 'None' },
          { value: 'bearer', label: 'Bearer Token' },
          { value: 'header', label: 'Custom Header' },
          { value: 'query',  label: 'Query Param' },
        ]} />
      </Field>
      {authType !== 'none' && (
        <>
          {(authType === 'header' || authType === 'query') && (
            <Field label="Key Name">
              <Input value={authKeyName} onChange={onAuthKeyName} placeholder="X-Api-Key" />
            </Field>
          )}
          <Field label="Token">
            <Input type="password" value={authValue} onChange={onAuthValue} placeholder="Token or secret" />
          </Field>
        </>
      )}
    </>
  );
}

function SyncBadge({ status, chunkCount }) {
  const label = status === 'ready' ? `${chunkCount ?? 0} chunks` : (status || 'idle');
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[status] || STATUS_BADGE.idle}`}>
      {label}
    </span>
  );
}

// ── "Add Source" modal ───────────────────────────────────────────────────────
const EMPTY_REST = { name: '', urls: '', method: 'GET', authType: 'none', authKeyName: '', authValue: '', responseDataPath: '', textField: '', syncIntervalMinutes: 0 };
const EMPTY_WEB  = { name: '', url: '', maxPages: 25, syncIntervalMinutes: 1440 };
const EMPTY_MCP  = { name: '', serverUrl: '', authType: 'none', authKeyName: '', authValue: '', resourceUris: '', syncIntervalMinutes: 0 };

function AddSourceModal({ kb, onClose, onRefresh }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'document' | 'rest' | 'mcp'
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restForm, setRestForm] = useState(EMPTY_REST);
  const [mcpForm, setMcpForm] = useState(EMPTY_MCP);
  const [webForm, setWebForm] = useState(EMPTY_WEB);
  const fileRef = useRef();

  const setR = key => val => setRestForm(f => ({ ...f, [key]: val }));
  const setM = key => val => setMcpForm(f => ({ ...f, [key]: val }));
  const setW = key => val => setWebForm(f => ({ ...f, [key]: val }));

  const addWeb = async () => {
    if (!webForm.url.trim()) return;
    setSaving(true);
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/web-sources`, webForm);
      onRefresh();
      onClose();
    } finally { setSaving(false); }
  };

  const uploadDocs = async () => {
    const files = fileRef.current?.files;
    if (!files?.length) return;
    setUploading(true);
    const form = new FormData();
    Array.from(files).forEach(f => form.append('files', f));
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onRefresh();
      onClose();
    } finally { setUploading(false); }
  };

  const addRest = async () => {
    const urlList = (restForm.urls || '').split('\n').map(u => u.trim()).filter(Boolean);
    if (!urlList.length) return;
    setSaving(true);
    try {
      // Create one source per URL so each can be synced/removed independently
      await Promise.all(urlList.map((url, i) =>
        api.post(`/support/knowledge-bases/${kb._id}/rest-api-sources`, {
          ...restForm,
          url,
          name: urlList.length === 1 ? (restForm.name || '') : `${restForm.name || 'REST API'} ${i + 1}`,
          urls: undefined,
        })
      ));
      onRefresh();
      onClose();
    } finally { setSaving(false); }
  };

  const addMcp = async () => {
    if (!mcpForm.serverUrl) return;
    setSaving(true);
    const payload = {
      ...mcpForm,
      resourceUris: mcpForm.resourceUris ? mcpForm.resourceUris.split('\n').map(s => s.trim()).filter(Boolean) : [],
    };
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/mcp-sources`, payload);
      onRefresh();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg max-h-[100dvh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex-shrink-0">
          <div>
            {step !== 'pick' && (
              <button onClick={() => setStep('pick')} className="text-xs text-[#0176D3] hover:underline mb-0.5 block">
                ← Back
              </button>
            )}
            <h2 className="text-base font-bold text-[#080707]">
              {step === 'pick'     && 'Add Source'}
              {step === 'document' && 'Upload Documents'}
              {step === 'web'      && 'Read a Website'}
              {step === 'rest'     && 'Connect REST API'}
              {step === 'mcp'      && 'Connect MCP Server'}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#5C5C5C]"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* Step 1 — Pick source type */}
          {step === 'pick' && (
            <div className="space-y-3">
              <p className="text-sm text-[#706E6B] mb-4">Choose the type of source you want to add to this knowledge base.</p>
              {SOURCE_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => setStep(t.key)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 border-[#F3F2F2] text-left transition-colors hover:border-[#D8DDE6] hover:${t.bg}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${t.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={20} className={t.color} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#2E2E2E]">{t.label}</p>
                      <p className="text-xs text-[#706E6B] mt-0.5">{t.description}</p>
                    </div>
                    <ChevronRight size={16} className="text-[#C9C9C9] ml-auto flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2a — Documents */}
          {step === 'document' && (
            <div>
              <div className="border-2 border-dashed border-[#E5E5E5] rounded-lg p-6 text-center mb-4">
                <FileText size={28} className="mx-auto text-[#C9C9C9] mb-2" />
                <p className="text-xs text-[#706E6B] mb-3">PDF, DOCX, TXT, CSV, XLSX — max 20 MB each</p>
                <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.csv,.xlsx" className="text-sm text-[#5C5C5C] mx-auto block" />
              </div>
              <button
                onClick={uploadDocs}
                disabled={uploading}
                className="w-full bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 rounded text-[13px] font-semibold"
              >
                {uploading ? 'Uploading…' : 'Upload & Index'}
              </button>
            </div>
          )}

          {/* Step 2a-web — Website */}
          {step === 'web' && (
            <div className="space-y-3">
              <Field label="Website address *">
                <Input value={webForm.url} onChange={setW('url')} placeholder="https://www.example.com" />
                <p className="text-xs text-[#706E6B] mt-1">Public pages on this site only. We follow its sitemap (or links), respect robots.txt, and only re-index pages that changed.</p>
              </Field>
              <Field label="Name (optional)">
                <Input value={webForm.name} onChange={setW('name')} placeholder="Company website" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Max pages (1-100)">
                  <Input type="number" value={webForm.maxPages} onChange={v => setW('maxPages')(Number(v))} placeholder="25" />
                </Field>
                <Field label="Re-check every (minutes, 0 = manual)">
                  <Input type="number" value={webForm.syncIntervalMinutes} onChange={v => setW('syncIntervalMinutes')(Number(v))} placeholder="1440" />
                </Field>
              </div>
              <button
                onClick={addWeb}
                disabled={saving || !webForm.url.trim()}
                className="w-full bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 rounded text-[13px] font-semibold mt-2"
              >
                {saving ? 'Adding…' : 'Add & Read Site'}
              </button>
            </div>
          )}

          {/* Step 2b — REST API */}
          {step === 'rest' && (
            <div className="space-y-3">
              <Field label="Name (optional prefix)">
                <Input value={restForm.name} onChange={setR('name')} placeholder="My API" />
              </Field>
              <Field label="Endpoint URL(s) — one per line for multiple">
                <textarea
                  value={restForm.urls} onChange={e => setR('urls')(e.target.value)} rows={3}
                  placeholder={"https://api.example.com/products\nhttps://api.example.com/categories"}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] font-mono"
                />
                <p className="text-xs text-[#706E6B] mt-1">Each URL becomes a separate indexed source — all share the same auth &amp; settings below.</p>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Method">
                  <Select value={restForm.method} onChange={setR('method')} options={[
                    { value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' },
                  ]} />
                </Field>
                <Field label="Auto-sync (minutes, 0 = manual)">
                  <Input type="number" value={restForm.syncIntervalMinutes} onChange={v => setR('syncIntervalMinutes')(Number(v))} placeholder="0" />
                </Field>
              </div>
              <AuthFields
                authType={restForm.authType} onAuthType={setR('authType')}
                authKeyName={restForm.authKeyName} onAuthKeyName={setR('authKeyName')}
                authValue={restForm.authValue} onAuthValue={setR('authValue')}
              />
              <Field label="Response Data Path (dot-path to array, blank = whole body)">
                <Input value={restForm.responseDataPath} onChange={setR('responseDataPath')} placeholder="data.items" />
              </Field>
              <Field label="Text Field (dot-path within each record, blank = stringify record)">
                <Input value={restForm.textField} onChange={setR('textField')} placeholder="description" />
              </Field>
              <button
                onClick={addRest}
                disabled={saving || !restForm.urls.trim()}
                className="w-full bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 rounded text-[13px] font-semibold mt-2"
              >
                {saving ? 'Adding…' : 'Add & Sync'}
              </button>
            </div>
          )}

          {/* Step 2c — MCP Server */}
          {step === 'mcp' && (
            <div className="space-y-3">
              <Field label="Name">
                <Input value={mcpForm.name} onChange={setM('name')} placeholder="My MCP Server" />
              </Field>
              <Field label="Server URL *">
                <Input value={mcpForm.serverUrl} onChange={setM('serverUrl')} placeholder="https://mcp.example.com/sse" />
              </Field>
              <Field label="Auto-sync (minutes, 0 = manual)">
                <Input type="number" value={mcpForm.syncIntervalMinutes} onChange={v => setM('syncIntervalMinutes')(Number(v))} placeholder="0" />
              </Field>
              <AuthFields
                authType={mcpForm.authType} onAuthType={setM('authType')}
                authKeyName={mcpForm.authKeyName} onAuthKeyName={setM('authKeyName')}
                authValue={mcpForm.authValue} onAuthValue={setM('authValue')}
              />
              <Field label="Resource URIs to index (one per line, blank = all resources)">
                <textarea
                  value={mcpForm.resourceUris}
                  onChange={e => setM('resourceUris')(e.target.value)}
                  rows={3}
                  placeholder={"resource://docs/intro\nresource://faq"}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] font-mono"
                />
              </Field>
              <button
                onClick={addMcp}
                disabled={saving || !mcpForm.serverUrl}
                className="w-full bg-[#0176D3] hover:bg-[#0176D3] disabled:opacity-50 text-white h-9 rounded text-[13px] font-semibold mt-2"
              >
                {saving ? 'Adding…' : 'Add & Sync'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Unified source list ──────────────────────────────────────────────────────
function SourceList({ kb, onRefresh }) {
  const [syncing, setSyncing] = useState(null);

  const syncRest = async (srcId) => {
    setSyncing(srcId);
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/rest-api-sources/${srcId}/sync`);
      setTimeout(onRefresh, 1500);
    } finally { setSyncing(null); }
  };

  const syncWeb = async (srcId) => {
    setSyncing(srcId);
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/web-sources/${srcId}/sync`);
      setTimeout(onRefresh, 1500);
    } finally { setSyncing(null); }
  };

  const removeWeb = async (srcId) => {
    if (!(await alertService.confirm('Remove?', 'Remove this website and all the pages indexed from it?', 'danger', 'Remove'))) return;
    await api.delete(`/support/knowledge-bases/${kb._id}/web-sources/${srcId}`);
    onRefresh();
  };

  const syncMcp = async (srcId) => {
    setSyncing(srcId);
    try {
      await api.post(`/support/knowledge-bases/${kb._id}/mcp-sources/${srcId}/sync`);
      setTimeout(onRefresh, 1500);
    } finally { setSyncing(null); }
  };

  const removeRest = async (srcId) => {
    if (!(await alertService.confirm('Remove?', 'Remove this REST API source and its indexed chunks?', 'danger', 'Remove'))) return;
    await api.delete(`/support/knowledge-bases/${kb._id}/rest-api-sources/${srcId}`);
    onRefresh();
  };

  const removeMcp = async (srcId) => {
    if (!(await alertService.confirm('Remove?', 'Remove this MCP server source and its indexed chunks?', 'danger', 'Remove'))) return;
    await api.delete(`/support/knowledge-bases/${kb._id}/mcp-sources/${srcId}`);
    onRefresh();
  };

  const allSources = [
    ...(kb.files || []).map(f => ({ type: 'document', id: f._id || f.originalName, ...f })),
    ...(kb.webSources || []).map(s => ({ type: 'web', ...s })),
    ...(kb.restApiSources || []).map(s => ({ type: 'rest', ...s })),
    ...(kb.mcpSources || []).map(s => ({ type: 'mcp', ...s })),
  ];

  if (allSources.length === 0) {
    return (
      <div className="text-center py-10 text-[#706E6B]">
        <div className="w-12 h-12 bg-[#F3F2F2] rounded-lg flex items-center justify-center mx-auto mb-3">
          <Plus size={20} className="text-[#C9C9C9]" />
        </div>
        <p className="text-sm">No sources yet</p>
        <p className="text-xs mt-1">Click "Add Source" to connect documents, REST APIs, or MCP servers</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {allSources.map((src, i) => {
        if (src.type === 'document') {
          return (
            <div key={i} className="flex items-center justify-between p-3.5 bg-[#F7F9FB] rounded-lg border border-[#F3F2F2]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-white border border-[#E5E5E5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText size={14} className="text-[#706E6B]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E] truncate">{src.originalName}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#706E6B]">Document</span>
                    <SyncBadge status={src.status} chunkCount={src.chunkCount} />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (src.type === 'web') {
          return (
            <div key={src._id} className="flex items-center justify-between p-3.5 bg-[#E3F3E8] rounded-lg border border-[#BDE3C8]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-white border border-[#BDE3C8] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe size={14} className="text-[#2E844A]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E] truncate">{src.name || 'Website'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#2E844A] truncate max-w-[180px]">{src.url}</span>
                    <SyncBadge status={src.syncStatus} chunkCount={src.chunkCount} />
                    {src.pageCount > 0 && <span className="text-xs text-[#706E6B]">{src.pageCount} pages</span>}
                  </div>
                  {src.syncError && <p className="text-xs text-[#BA0517] mt-0.5">{src.syncError}</p>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-2">
                <button onClick={() => syncWeb(src._id)} disabled={syncing === src._id} title="Re-read now"
                  className="p-1.5 text-[#706E6B] hover:text-[#0176D3] rounded-lg hover:bg-white disabled:opacity-50">
                  <RefreshCw size={13} className={syncing === src._id ? 'animate-spin' : ''} />
                </button>
                <button onClick={() => removeWeb(src._id)} title="Remove"
                  className="p-1.5 text-[#706E6B] hover:text-[#BA0517] rounded-lg hover:bg-white">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        }

        if (src.type === 'rest') {
          return (
            <div key={src._id} className="flex items-center justify-between p-3.5 bg-[#EAF4FE] rounded-lg border border-[#B8DAF3]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-white border border-[#B8DAF3] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe size={14} className="text-[#0176D3]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E] truncate">{src.name || 'REST API'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#0176D3] truncate max-w-[180px]">{src.url}</span>
                    <SyncBadge status={src.syncStatus} chunkCount={src.chunkCount} />
                  </div>
                  {src.syncError && <p className="text-xs text-[#BA0517] mt-0.5">{src.syncError}</p>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-2">
                <button onClick={() => syncRest(src._id)} disabled={syncing === src._id} title="Sync now"
                  className="p-1.5 text-[#706E6B] hover:text-[#0176D3] rounded-lg hover:bg-white disabled:opacity-50">
                  <RefreshCw size={13} className={syncing === src._id ? 'animate-spin' : ''} />
                </button>
                <button onClick={() => removeRest(src._id)} title="Remove"
                  className="p-1.5 text-[#706E6B] hover:text-[#BA0517] rounded-lg hover:bg-white">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        }

        if (src.type === 'mcp') {
          return (
            <div key={src._id} className="flex items-center justify-between p-3.5 bg-[#EEF4FF] rounded-lg border border-[#EEF4FF]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-white border border-[#EEF4FF] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Server size={14} className="text-[#0176D3]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E] truncate">{src.name || 'MCP Server'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#5C5C5C] truncate max-w-[180px]">{src.serverUrl}</span>
                    <SyncBadge status={src.syncStatus} chunkCount={src.chunkCount} />
                  </div>
                  {src.syncError && <p className="text-xs text-[#BA0517] mt-0.5">{src.syncError}</p>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-2">
                <button onClick={() => syncMcp(src._id)} disabled={syncing === src._id} title="Sync now"
                  className="p-1.5 text-[#706E6B] hover:text-[#0176D3] rounded-lg hover:bg-white disabled:opacity-50">
                  <RefreshCw size={13} className={syncing === src._id ? 'animate-spin' : ''} />
                </button>
                <button onClick={() => removeMcp(src._id)} title="Remove"
                  className="p-1.5 text-[#706E6B] hover:text-[#BA0517] rounded-lg hover:bg-white">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function KnowledgeBasePage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('agentic_mode');
  const [kbs, setKbs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [querying, setQuerying] = useState(false);

  const load = async () => {
    const { data } = await api.get('/bot/knowledge-bases');
    const list = data.data || [];
    setKbs(list);
    if (selected) {
      const fresh = list.find(k => k._id === selected._id);
      if (fresh) setSelected(fresh);
    }
  };

  const loadSelected = async () => {
    if (!selected) return;
    const { data } = await api.get(`/support/knowledge-bases/${selected._id}`);
    if (data.data) {
      setSelected(data.data);
      setKbs(prev => prev.map(k => k._id === data.data._id ? data.data : k));
    }
  };

  useEffect(() => { if (hasAccess) load(); }, [hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Knowledge Base"
        subtitle="AI answers grounded in your own documents, APIs and MCP servers"
        icon={Brain}
        plan={plan}
        featureName="Agentic Mode"
        description="Upgrade to unlock AI-powered answers grounded in your own uploaded documents, REST APIs, and MCP server resources."
        highlights={[
          { icon: FileText, label: 'Upload documents', desc: 'PDF, DOCX and TXT, automatically indexed' },
          { icon: Globe, label: 'REST API sources', desc: 'Pull and index data from any HTTP endpoint' },
          { icon: Server, label: 'MCP server sources', desc: 'Index resources from any MCP-compatible server' },
        ]}
      />
    );
  }

  const create = async () => {
    if (!name.trim()) return;
    await api.post('/bot/knowledge-bases', { name, description });
    setShowCreate(false); setName(''); setDescription('');
    load();
  };

  const remove = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this knowledge base?', 'danger', 'Delete'))) return;
    await api.delete(`/bot/knowledge-bases/${id}`);
    if (selected?._id === id) setSelected(null);
    load();
  };

  const askQuery = async () => {
    if (!query.trim()) return;
    setQuerying(true); setAnswer(null);
    try {
      const { data } = await api.post('/bot/knowledge-bases/query', { question: query, knowledgeBaseId: selected?._id });
      setAnswer(data.data);
    } finally { setQuerying(false); }
  };

  const totalSources = (kb) =>
    (kb.files?.length || 0) + (kb.webSources?.length || 0) + (kb.restApiSources?.length || 0) + (kb.mcpSources?.length || 0);

  return (
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden bg-[#F7F9FB]">
      {/* Sidebar */}
      <div className="w-full lg:w-64 max-h-[40vh] lg:max-h-none flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E5E5E5] flex flex-col">
        <div className="p-4 border-b border-[#E5E5E5]">
          <h2 className="text-base font-bold text-[#2E2E2E] mb-2">Knowledge Bases</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="w-full bg-[#0176D3] hover:bg-[#014486] text-white py-1.5 rounded text-[13px] font-semibold flex items-center justify-center gap-1"
          >
            <Plus size={14} /> New KB
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {kbs.map(kb => (
            <div
              key={kb._id}
              onClick={() => { setSelected(kb); setAnswer(null); }}
              className={`p-3 rounded-lg cursor-pointer mb-1 transition-colors ${selected?._id === kb._id ? 'bg-[#EAF4FE] border border-[#B8DAF3]' : 'hover:bg-[#F7F9FB] border border-transparent'}`}
            >
              <p className="text-sm font-medium text-[#2E2E2E] truncate">{kb.name}</p>
              <div className="flex gap-2 mt-0.5 flex-wrap">
                {kb.files?.length > 0 && (
                  <span className="text-xs text-[#706E6B] flex items-center gap-0.5">
                    <FileText size={10} /> {kb.files.length}
                  </span>
                )}
                {kb.restApiSources?.length > 0 && (
                  <span className="text-xs text-[#0176D3] flex items-center gap-0.5">
                    <Globe size={10} /> {kb.restApiSources.length}
                  </span>
                )}
                {kb.mcpSources?.length > 0 && (
                  <span className="text-xs text-[#5C5C5C] flex items-center gap-0.5">
                    <Server size={10} /> {kb.mcpSources.length}
                  </span>
                )}
                {totalSources(kb) === 0 && <span className="text-xs text-[#C9C9C9]">empty</span>}
              </div>
            </div>
          ))}
          {kbs.length === 0 && <p className="text-xs text-[#706E6B] text-center py-6">No knowledge bases yet</p>}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 overflow-y-auto">
        {!selected && (
          <div className="flex items-center justify-center h-full text-[#706E6B]">
            <div className="text-center">
              <Brain size={40} className="mx-auto mb-3 opacity-30" />
              <p>Select a knowledge base to manage its sources</p>
              <p className="text-xs mt-1">Each KB can combine documents, REST APIs, and MCP servers</p>
            </div>
          </div>
        )}

        {selected && (
          <div className="p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
              <div>
                <h1 className="text-xl font-bold text-[#2E2E2E]">{selected.name}</h1>
                {selected.description && <p className="text-sm text-[#706E6B] mt-1">{selected.description}</p>}
              </div>
              <button onClick={() => remove(selected._id)} className="text-sm text-[#BA0517] hover:text-[#8E030F]">Delete KB</button>
            </div>

            {/* Sources section */}
            <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#2E2E2E]">Sources</h3>
                  <p className="text-xs text-[#706E6B] mt-0.5">
                    {totalSources(selected) === 0
                      ? 'No sources added yet'
                      : `${totalSources(selected)} source${totalSources(selected) !== 1 ? 's' : ''} connected`}
                  </p>
                </div>
                <button
                  onClick={() => setShowAddSource(true)}
                  className="flex items-center gap-1.5 bg-[#0176D3] hover:bg-[#014486] text-white px-3.5 py-1.5 rounded text-[13px] font-semibold whitespace-nowrap"
                >
                  <Plus size={14} /> Add Source
                </button>
              </div>
              <SourceList kb={selected} onRefresh={loadSelected} />
            </div>

            {/* Test query */}
            <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <h3 className="font-semibold text-[#444444] mb-3">Test Query</h3>
              <div className="flex gap-2">
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && askQuery()}
                  placeholder="Ask a question across all sources…"
                  className="flex-1 border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
                <button onClick={askQuery} disabled={querying} className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-4 py-2 rounded text-[13px] font-semibold whitespace-nowrap">
                  {querying ? '…' : 'Ask'}
                </button>
              </div>
              {answer && (
                <div className="mt-4 p-4 bg-[#EAF4FE] rounded-lg">
                  <p className="text-sm text-[#2E2E2E] leading-relaxed">{answer.answer}</p>
                  <p className="text-xs text-[#706E6B] mt-2">
                    Confidence: {(answer.confidence * 100).toFixed(0)}% · Sources: {answer.sources?.join(', ') || '—'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create KB modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto p-4 sm:p-6">
            <h2 className="text-[16px] font-bold text-[#2E2E2E] mb-1">New Knowledge Base</h2>
            <p className="text-xs text-[#706E6B] mb-4">You can add sources (documents, REST APIs, MCP servers) after creation.</p>
            <div className="space-y-3">
              <Field label="Name *">
                <Input value={name} onChange={setName} placeholder="e.g. Product Documentation" />
              </Field>
              <Field label="Description">
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
              </Field>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowCreate(false); setName(''); setDescription(''); }}
                className="flex-1 border border-[#E5E5E5] rounded-lg py-2 text-sm text-[#5C5C5C] hover:bg-[#F7F9FB]">Cancel</button>
              <button onClick={create} disabled={!name.trim()}
                className="flex-1 bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white rounded py-2 text-[13px] font-semibold">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Source modal */}
      {showAddSource && selected && (
        <AddSourceModal
          kb={selected}
          onClose={() => setShowAddSource(false)}
          onRefresh={loadSelected}
        />
      )}
    </div>
  );
}
